import { useEffect, useRef, useState } from 'react';
import { useCityStore } from '../stores/cityStore';
import { City } from '../types/profile';

const getSelectedOptions = (options: City[], profileCities: City[] = []): City[] => {
  return options.filter(opt => profileCities?.some((s: City) => s.id === opt.id));
};

export const useCitySelector = (profileCities: City[] = []) => {
  const { cities, loading, hasSettledCitiesLoad, fetchCities } = useCityStore();

  const options = cities
    .map(city => ({
      id: city.id,
      name: city.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const [selectedOptions, setSelectedOptions] = useState<City[]>(
    getSelectedOptions(options, profileCities)
  );

  // Once the initial profileCities selection has been applied (either
  // synchronously above, or below once the city list finishes loading),
  // stop re-applying it. Without this guard, clearing the selection to
  // type a new city (selectedOptions.length hits 0) re-triggers the effect
  // below, which immediately restores the original city from profileCities
  // — making it impossible to ever change the selection on an edit form
  // where a city is pre-filled.
  const hasHydratedRef = useRef(selectedOptions.length > 0);

  useEffect(() => {
    if (!cities.length) {
      fetchCities();
    }
  }, [cities.length, fetchCities]);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (!selectedOptions.length) {
      const initialSelectedOptions = getSelectedOptions(options, profileCities);
      if (initialSelectedOptions.length) {
        setSelectedOptions(initialSelectedOptions);
        hasHydratedRef.current = true;
      }
    }
  }, [profileCities, options, selectedOptions.length]);

  return {
    options,
    selectedOptions,
    setSelectedOptions,
    loading,
    hasSettledOptionsLoad: hasSettledCitiesLoad || cities.length > 0,
  };
};
